import React, { useEffect, useState } from 'react';
import { UserPlus, CheckCircle, LogOut, User, Sparkles, Shield, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabase';
interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}
interface PatientFormData {
  name: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  emergencyContact: EmergencyContact;
}
export function PatientSelfRegisterPage() {
  const {
    logout,
    user
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredPatientName, setRegisteredPatientName] = useState('');
  // New state to track if patient already has a record
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [existingPatientData, setExistingPatientData] = useState<any>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);
  const [formData, setFormData] = useState<PatientFormData>({
    name: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });
  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };
  const handleEmergencyContactChange = (field: keyof EmergencyContact, value: string) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
    setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      // Validate required fields
      if (!formData.name || !formData.gender || !formData.phone) {
        setError('Por favor complete los campos obligatorios: Nombre, Género y Teléfono');
        setIsSubmitting(false);
        return;
      }
      // Direct Supabase insert bypassing DataContext permission checks
      const {
        data: insertedPatient,
        error: insertError
      } = await supabase.from('patients').insert([{
        name: formData.name,
        gender: formData.gender,
        phone: formData.phone,
        email: formData.email || null,
        address: formData.address || null,
        emergency_contact: formData.emergencyContact,
        created_at: new Date().toISOString()
      }]).select().single();
      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setError(`Error al registrar: ${insertError.message}`);
        setIsSubmitting(false);
        return;
      }
      // Create audit log entry
      try {
        await supabase.from('audit_logs').insert([{
          user_id: user?.id || 'paciente-system',
          user_name: user?.name || 'Sistema de Registro de Pacientes',
          action: 'create',
          module: 'patients',
          entity_id: insertedPatient.id,
          entity_name: formData.name,
          details: `Paciente registrado mediante auto-registro: ${formData.name} (${formData.phone})`,
          created_at: new Date().toISOString()
        }]);
      } catch (auditError) {
        console.error('Error creating audit log:', auditError);
        // Don't fail the registration if audit log fails
      }
      setRegisteredPatientName(formData.name);
      setIsSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al registrar paciente');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleRegisterAnother = () => {
    setIsSuccess(false);
    setRegisteredPatientName('');
    setFormData({
      name: '',
      gender: '',
      phone: '',
      email: '',
      address: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    });
    setError(null);
  };
  // Check if patient already has a record on mount
  useEffect(() => {
    const checkExistingPatient = async () => {
      if (!user?.email) {
        setIsCheckingExisting(false);
        return;
      }
      try {
        const {
          data,
          error: queryError
        } = await supabase.from('patients').select('*').eq('email', user.email).maybeSingle();
        if (queryError) {
          console.error('Error checking existing patient:', queryError);
          setIsCheckingExisting(false);
          return;
        }
        if (data) {
          // Patient already has a record
          setAlreadyRegistered(true);
          setExistingPatientData(data);
        }
      } catch (err) {
        console.error('Error in checkExistingPatient:', err);
      } finally {
        setIsCheckingExisting(false);
      }
    };
    checkExistingPatient();
  }, [user?.email]);
  // Show loading state while checking
  if (isCheckingExisting) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4249">
        <div className="bg-white rounded-3xl shadow-2xl p-10 text-center" data-id="element-4250">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-4251" />
          <p className="text-gray-600 font-medium" data-id="element-4252">
            Verificando información...
          </p>
        </div>
      </div>;
  }
  // Show "already registered" screen if patient has a record
  if (alreadyRegistered && existingPatientData) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4253">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-teal-100" data-id="element-4254">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-10 text-center overflow-hidden" data-id="element-4255">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-4256"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-4257"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-4258"></div>

            <div className="relative z-10" data-id="element-4259">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-md rounded-full mb-6" data-id="element-4260">
                <CheckCircle2 className="w-14 h-14 text-white" strokeWidth={2.5} data-id="element-4261" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-3" data-id="element-4262">
                Ya está Registrado
              </h1>
              <div className="flex items-center justify-center gap-2 text-teal-50" data-id="element-4263">
                <Sparkles className="w-4 h-4" data-id="element-4264" />
                <p className="text-sm font-medium" data-id="element-4265">Bienvenido de nuevo</p>
                <Sparkles className="w-4 h-4" data-id="element-4266" />
              </div>
            </div>
          </div>

          <div className="p-8" data-id="element-4267">
            {/* Patient Info */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl p-6 mb-6" data-id="element-4268">
              <div className="flex items-start gap-4" data-id="element-4269">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center" data-id="element-4270">
                  <User className="w-6 h-6 text-teal-600" data-id="element-4271" />
                </div>
                <div className="flex-1" data-id="element-4272">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-4273">
                    {existingPatientData.name}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4274">
                    Su información ya está registrada en nuestro sistema. Si
                    necesita actualizar sus datos, por favor contacte a nuestro
                    equipo.
                  </p>
                  {existingPatientData.phone && <p className="text-sm text-gray-500 mt-2" data-id="element-4275">
                      Teléfono: {existingPatientData.phone}
                    </p>}
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6 pb-6 border-b border-gray-100" data-id="element-4276">
              <Shield className="w-4 h-4 text-teal-600" data-id="element-4277" />
              <span data-id="element-4278">Sus datos están protegidos y seguros</span>
            </div>

            {/* Logout Button Only */}
            <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-3 shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:-translate-y-0.5" data-id="element-4279">
              <LogOut className="w-5 h-5" data-id="element-4280" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>;
  }
  if (isSuccess) {
    return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4281">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-teal-100" data-id="element-4282">
          {/* Success Header with Animation */}
          <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-10 text-center overflow-hidden" data-id="element-4283">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-4284"></div>
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-4285"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-4286"></div>

            <div className="relative z-10" data-id="element-4287">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-md rounded-full mb-6 animate-bounce" data-id="element-4288">
                <CheckCircle className="w-14 h-14 text-white" strokeWidth={2.5} data-id="element-4289" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-3" data-id="element-4290">
                ¡Registro Exitoso!
              </h1>
              <div className="flex items-center justify-center gap-2 text-teal-50" data-id="element-4291">
                <Sparkles className="w-4 h-4" data-id="element-4292" />
                <p className="text-sm font-medium" data-id="element-4293">Bienvenido a Savidna</p>
                <Sparkles className="w-4 h-4" data-id="element-4294" />
              </div>
            </div>
          </div>

          <div className="p-8" data-id="element-4295">
            {/* Success Message */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl p-6 mb-6" data-id="element-4296">
              <div className="flex items-start gap-4" data-id="element-4297">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center" data-id="element-4298">
                  <User className="w-6 h-6 text-teal-600" data-id="element-4299" />
                </div>
                <div className="flex-1" data-id="element-4300">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-4301">
                    {registeredPatientName}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4302">
                    Su información ha sido registrada correctamente en nuestro
                    sistema. Nuestro equipo se pondrá en contacto con usted
                    próximamente.
                  </p>
                </div>
              </div>
            </div>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6 pb-6 border-b border-gray-100" data-id="element-4303">
              <Shield className="w-4 h-4 text-teal-600" data-id="element-4304" />
              <span data-id="element-4305">Sus datos están protegidos y seguros</span>
            </div>

            {/* Only Logout Button - Remove "Registrar Otro Paciente" */}
            <button onClick={logout} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-3 shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:-translate-y-0.5" data-id="element-4306">
              <LogOut className="w-5 h-5" data-id="element-4307" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4" data-id="element-4308">
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden border border-teal-100" data-id="element-4309">
        {/* Modern Header */}
        <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 p-10 text-center overflow-hidden" data-id="element-4310">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" data-id="element-4311"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-4312"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" data-id="element-4313"></div>

          <div className="relative z-10" data-id="element-4314">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-md rounded-2xl mb-4" data-id="element-4315">
              <User className="w-10 h-10 text-white" strokeWidth={2} data-id="element-4316" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2" data-id="element-4317">
              Registro de Paciente
            </h1>
            <p className="text-teal-50 text-lg font-medium" data-id="element-4318">Sistema Savidna</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full text-sm text-teal-50" data-id="element-4319">
              <Shield className="w-4 h-4" data-id="element-4320" />
              <span data-id="element-4321">Información segura y confidencial</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 md:p-10" data-id="element-4322">
          {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg" data-id="element-4323">
              <div className="flex items-start gap-3" data-id="element-4324">
                <div className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5" data-id="element-4325">
                  <span className="text-red-600 text-xs font-bold" data-id="element-4326">!</span>
                </div>
                <p className="text-sm text-red-700 font-medium" data-id="element-4327">{error}</p>
              </div>
            </div>}

          <div className="space-y-8" data-id="element-4328">
            {/* Información Personal */}
            <div className="relative" data-id="element-4329">
              <div className="flex items-center gap-3 mb-5" data-id="element-4330">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl text-white font-bold shadow-lg shadow-teal-500/25" data-id="element-4331">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4332">
                  Información Personal
                </h3>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-teal-50/30 rounded-2xl p-6 border border-gray-100" data-id="element-4333">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4334">
                  <div className="md:col-span-2" data-id="element-4335">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4336">
                      Nombre Completo <span className="text-red-500" data-id="element-4337">*</span>
                    </label>
                    <input type="text" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" placeholder="Ingrese su nombre completo" required data-id="element-4338" />
                  </div>

                  <div data-id="element-4339">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4340">
                      Género <span className="text-red-500" data-id="element-4341">*</span>
                    </label>
                    <select value={formData.gender} onChange={e => handleInputChange('gender', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" required data-id="element-4342">
                      <option value="" data-id="element-4343">Seleccionar</option>
                      <option value="masculino" data-id="element-4344">Masculino</option>
                      <option value="femenino" data-id="element-4345">Femenino</option>
                      <option value="otro" data-id="element-4346">Otro</option>
                    </select>
                  </div>

                  <div data-id="element-4347">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4348">
                      Teléfono <span className="text-red-500" data-id="element-4349">*</span>
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white" placeholder="809-000-0000" required data-id="element-4350" />
                  </div>
                </div>
              </div>
            </div>

            {/* Información de Contacto */}
            <div className="relative" data-id="element-4351">
              <div className="flex items-center gap-3 mb-5" data-id="element-4352">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl text-white font-bold shadow-lg shadow-cyan-500/25" data-id="element-4353">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4354">
                  Información de Contacto
                </h3>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-cyan-50/30 rounded-2xl p-6 border border-gray-100" data-id="element-4355">
                <div className="grid grid-cols-1 gap-5" data-id="element-4356">
                  <div data-id="element-4357">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4358">
                      Email
                    </label>
                    <input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white" placeholder="correo@ejemplo.com" data-id="element-4359" />
                  </div>

                  <div data-id="element-4360">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4361">
                      Dirección
                    </label>
                    <input type="text" value={formData.address} onChange={e => handleInputChange('address', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-white" placeholder="Calle, número, sector, ciudad" data-id="element-4362" />
                  </div>
                </div>
              </div>
            </div>

            {/* Contacto de Emergencia */}
            <div className="relative" data-id="element-4363">
              <div className="flex items-center gap-3 mb-5" data-id="element-4364">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white font-bold shadow-lg shadow-rose-500/25" data-id="element-4365">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-4366">
                  Contacto de Emergencia
                </h3>
              </div>
              <div className="bg-gradient-to-br from-rose-50/50 to-pink-50/30 rounded-2xl p-6 border border-rose-100" data-id="element-4367">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-4368">
                  <div data-id="element-4369">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4370">
                      Nombre
                    </label>
                    <input type="text" value={formData.emergencyContact.name} onChange={e => handleEmergencyContactChange('name', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Nombre del contacto" data-id="element-4371" />
                  </div>

                  <div data-id="element-4372">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4373">
                      Relación
                    </label>
                    <input type="text" value={formData.emergencyContact.relationship} onChange={e => handleEmergencyContactChange('relationship', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Ej: Madre, Esposo/a, Hijo/a" data-id="element-4374" />
                  </div>

                  <div className="md:col-span-2" data-id="element-4375">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4376">
                      Teléfono
                    </label>
                    <input type="tel" value={formData.emergencyContact.phone} onChange={e => handleEmergencyContactChange('phone', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all bg-white" placeholder="Número de contacto de emergencia" data-id="element-4377" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-4378">
            <button type="submit" disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-xl shadow-teal-500/25 hover:shadow-2xl hover:shadow-teal-500/30 hover:-translate-y-0.5" data-id="element-4379">
              {isSubmitting ? <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" data-id="element-4380" />
                  Registrando...
                </> : <>
                  <UserPlus className="w-6 h-6" data-id="element-4381" />
                  Registrar Paciente
                </>}
            </button>

            <button type="button" onClick={logout} className="sm:w-auto bg-gray-50 text-gray-700 py-4 px-8 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-200 flex items-center justify-center gap-3 border-2 border-gray-200" data-id="element-4382">
              <LogOut className="w-5 h-5" data-id="element-4383" />
              Salir
            </button>
          </div>
        </form>
      </div>
    </div>;
}